import React from 'react';
import {
  BuildCircleOutlined,
  ExpandLess,
  ExpandMore,
  RecyclingOutlined,
  ShieldOutlined,
} from '@mui/icons-material';
import { Box, Button, Collapse, MenuItem, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import MultipleFilesUpload from '../components/MultipleFilesUpload';
import { emptyProductForm } from '../utils';
import { Desc, Title } from './index.styles';
import { StyledTextField } from './productInformation';
import { useProductFieldError } from './validation';
import { ComplianceField, EXCEL_ROW_BY_FIELD_NAME } from 'src/Lca/compliance';

interface Props {
  values: typeof emptyProductForm;
  setValues: Function;
}

type ProductForm = typeof emptyProductForm;
type ProductField = keyof ProductForm;

export default function Circularity({ values, setValues }: Props) {
  const { t } = useTranslation('lca', { keyPrefix: 'circularity' });
  const [openSections, setOpenSections] = React.useState({
    durability: false,
    recycling: false,
    materials: false,
  });
  const expectedProductLifetimeError = useProductFieldError(
    'expected_product_lifetime_years',
    values.expected_product_lifetime_years
  );

  const updateField = (
    name: ProductField,
    value: string,
    options?: { numericOnly?: boolean }
  ) => {
    const nextValue = options?.numericOnly ? value.replace(/\D+/g, '') : value;
    setValues({ ...values, [name]: nextValue });
  };

  const getFieldValue = (name: ProductField) => {
    const value = values[name];
    return typeof value === 'string' ? value : '';
  };

  const renderField = (
    name: ProductField,
    options?: {
      multiline?: boolean;
      rows?: number;
      type?: string;
      select?: boolean;
      stackedLabel?: boolean;
      helperText?: string;
      children?: React.ReactNode;
      errorText?: string;
      numericOnly?: boolean;
    }
  ) => {
    const fieldLabel = t(`fields.${name}`);
    const fieldId = `circularity-${name}`;
    let inputProps: Record<string, string | number> | undefined;
    if (options?.numericOnly) {
      inputProps = {
        inputMode: 'numeric',
        pattern: '[0-9]*',
      };
    } else if (options?.type === 'number') {
      inputProps = { min: 0 };
    }

    const field = (
      <ComplianceField
        excelRow={EXCEL_ROW_BY_FIELD_NAME[name]}
        regulatoryFrameworks={values.regulatory_frameworks}
      >
        <StyledTextField
          id={fieldId}
          fullWidth
          label={options?.stackedLabel ? undefined : fieldLabel}
          name={name}
          value={getFieldValue(name)}
          onChange={(e) =>
            updateField(name, e.target.value, {
              numericOnly: options?.numericOnly,
            })
          }
          multiline={options?.multiline}
          rows={options?.rows}
          type={options?.type}
          select={options?.select}
          error={!!options?.errorText}
          helperText={options?.errorText || options?.helperText}
          inputProps={{
            ...inputProps,
            'aria-label': fieldLabel,
          }}
        >
          {options?.children}
        </StyledTextField>
      </ComplianceField>
    );

    if (!options?.stackedLabel) {
      return field;
    }

    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '10px',
        }}
      >
        <Typography
          component="label"
          htmlFor={fieldId}
          sx={{
            color: '#6c5b4c',
            fontSize: '16px',
            fontWeight: 500,
            lineHeight: '24px',
          }}
        >
          {fieldLabel}
        </Typography>
        {field}
      </Box>
    );
  };

  const renderUploadField = ({
    name,
    attachmentType,
  }: {
    name: ProductField;
    attachmentType: string;
  }) => (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
      }}
    >
      {renderField(name, { multiline: true, rows: 4 })}
      <ComplianceField
        excelRow={EXCEL_ROW_BY_FIELD_NAME[`attachment: ${attachmentType}`]}
        regulatoryFrameworks={values.regulatory_frameworks}
      >
        <MultipleFilesUpload
          attachmentType={attachmentType}
          productId={values.id}
        />
      </ComplianceField>
    </Box>
  );

  const renderAdditionalToggle = (
    section: keyof typeof openSections,
    label: string
  ) => (
    <Button
      variant="text"
      onClick={() =>
        setOpenSections((current) => ({
          ...current,
          [section]: !current[section],
        }))
      }
      sx={{
        alignSelf: 'flex-start',
        p: 0,
        color: '#7e4308',
        fontWeight: 700,
        textTransform: 'none',
      }}
      endIcon={openSections[section] ? <ExpandLess /> : <ExpandMore />}
    >
      {label}
    </Button>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      <Title>{t('title')}</Title>
      <Desc>{t('desc')}</Desc>

      <SectionCard
        icon={<BuildCircleOutlined sx={{ color: '#7e4308' }} />}
        title={t('sections.productDurabilityRepairability')}
        subtitle={t('sectionDescriptions.productDurabilityRepairability')}
      >
        <FieldsGrid>
          {renderField('expected_product_lifetime_years', {
            type: 'text',
            numericOnly: true,
            helperText: t('helpers.expectedProductLifetimeYears'),
            errorText: expectedProductLifetimeError,
          })}
          {renderField('repairability_information', {
            select: true,
            helperText: t('helpers.repairabilityInformation'),
            children: [
              <MenuItem key="empty" value="">
                {t('repairabilityOptions.select')}
              </MenuItem>,
              <MenuItem key="1" value="1">
                {t('repairabilityOptions.1')}
              </MenuItem>,
              <MenuItem key="2" value="2">
                {t('repairabilityOptions.2')}
              </MenuItem>,
              <MenuItem key="3" value="3">
                {t('repairabilityOptions.3')}
              </MenuItem>,
              <MenuItem key="4" value="4">
                {t('repairabilityOptions.4')}
              </MenuItem>,
              <MenuItem key="5" value="5">
                {t('repairabilityOptions.5')}
              </MenuItem>,
            ],
          })}
          {renderField('availability_of_spare_parts', {
            multiline: true,
            rows: 4,
          })}
          {renderField(
            'compatibility_with_commonly_available_tools_and_spare_parts',
            {
              multiline: true,
              rows: 4,
            }
          )}
          {renderUploadField({
            name: 'repair_disassembly_instructions_description',
            attachmentType: 'repair-disassembly-instructions',
          })}
          {renderUploadField({
            name: 'proper_use_instructions',
            attachmentType: 'proper-use-instructions',
          })}
        </FieldsGrid>

        <AdditionalFieldsBlock>
          {renderAdditionalToggle(
            'durability',
            t('additionalFields.productDurabilityRepairability')
          )}
          <Collapse in={openSections.durability} timeout="auto" unmountOnExit>
            <FieldsGrid>
              {(
                [
                  'delivery_time_of_spare_parts',
                  'affordability_of_spare_parts',
                  'mean_time_between_failures',
                  'resistance_to_stress_or_aging_mechanisms',
                  'guaranteed_service_life',
                  'operating_life',
                  'actual_product_use_information',
                  'number_and_complexity_of_repair_processes',
                  'need_for_specialised_tools',
                  'conditions_for_access_to_product_data',
                  'conditions_for_access_to_required_equipment_and_software',
                  'functional_performance_of_product',
                  'compatibility_with_other_products_or_systems',
                  'required_user_skills',
                  'access_to_testing_protocols_or_specialised_testing_equipment',
                  'warranty_availability_for_refurbished_or_remanufactured_products',
                  'access_conditions_to_intellectual_property_protected_technologies',
                ] as ProductField[]
              ).map((fieldName) => (
                <React.Fragment key={fieldName}>
                  {renderField(fieldName, {
                    multiline: true,
                    rows: 4,
                    stackedLabel: true,
                  })}
                </React.Fragment>
              ))}
            </FieldsGrid>
          </Collapse>
        </AdditionalFieldsBlock>
      </SectionCard>

      <SectionCard
        icon={<RecyclingOutlined sx={{ color: '#7e4308' }} />}
        title={t('sections.recyclingEndOfLifeInformation')}
        subtitle={t('sectionDescriptions.recyclingEndOfLifeInformation')}
      >
        <FieldsGrid>
          {renderField('recycling_possibilities', {
            multiline: true,
            rows: 4,
          })}
          {renderUploadField({
            name: 'disassembly_instructions',
            attachmentType: 'disassembly-instructions',
          })}
          {renderField('epr_information_description', {
            multiline: true,
            rows: 4,
          })}
          {renderField('disposal_instructions', {
            multiline: true,
            rows: 4,
          })}
        </FieldsGrid>

        <AdditionalFieldsBlock>
          {renderAdditionalToggle(
            'recycling',
            t('additionalFields.recyclingEndOfLifeInformation')
          )}
          <Collapse in={openSections.recycling} timeout="auto" unmountOnExit>
            <FieldsGrid>
              {(
                [
                  'safe_easy_non_destructive_access_to_recyclable_components_materials_or_components_containing_hazardous_substances',
                  'material_composition_and_material_homogeneity',
                  'ability_to_sort_materials_while_maintaining_high_purity',
                  'ease_of_non_destructive_disassembly_and_reassembly',
                  'avoidance_of_technical_design_solutions_that_hinder_reuse_upgrade_repair_maintenance_refurbishment_remanufacturing_or_recycling_of_products_and_components',
                ] as ProductField[]
              ).map((fieldName) => (
                <React.Fragment key={fieldName}>
                  {renderField(fieldName, {
                    multiline: true,
                    rows: 4,
                    stackedLabel: true,
                  })}
                </React.Fragment>
              ))}
            </FieldsGrid>
          </Collapse>
        </AdditionalFieldsBlock>
      </SectionCard>

      <SectionCard
        icon={<ShieldOutlined sx={{ color: '#7e4308' }} />}
        title={t('sections.materialCompositionSafety')}
        subtitle={t('sectionDescriptions.materialCompositionSafety')}
      >
        <FieldsGrid>
          {renderField('hazardous_substances_level', {
            multiline: true,
            rows: 4,
          })}
          {renderField('recommended_fire_extinguishing_agents', {
            multiline: true,
            rows: 4,
          })}
          {renderUploadField({
            name: 'safety_labels_and_symbols',
            attachmentType: 'safety-labels-symbols',
          })}
          {renderUploadField({
            name: 'safe_use_instructions',
            attachmentType: 'safe-use-instructions',
          })}
        </FieldsGrid>

        <AdditionalFieldsBlock>
          {renderAdditionalToggle(
            'materials',
            t('additionalFields.materialCompositionSafety')
          )}
          <Collapse in={openSections.materials} timeout="auto" unmountOnExit>
            <FieldsGrid>
              {(
                [
                  'use_of_substances_of_concern_during_manufacturing_process',
                  'presence_of_substances_of_concern_in_product_as_substances_mixtures_or_components',
                  'presence_of_substances_of_concern_when_product_becomes_waste',
                  'impact_of_substances_of_concern_on_human_health_and_environment',
                  'amount_of_consumables_required_for_proper_operation_and_maintenance',
                  'characteristics_of_consumables',
                  'availability_of_consumables',
                  'performance_efficiency_of_consumables',
                  'technical_lifetime_of_consumables',
                  'reusability_of_consumables',
                  'repairability_of_consumables',
                  'remanufacturability_regenerability_of_consumables',
                ] as ProductField[]
              ).map((fieldName) => (
                <React.Fragment key={fieldName}>
                  {renderField(fieldName, {
                    multiline: true,
                    rows: 4,
                    stackedLabel: true,
                  })}
                </React.Fragment>
              ))}
            </FieldsGrid>
          </Collapse>
        </AdditionalFieldsBlock>
      </SectionCard>
    </Box>
  );
}

function SectionCard({
  icon,
  title,
  subtitle,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  children: React.ReactNode;
}) {
  return (
    <Box
      sx={{
        border: '1px solid #ece1d5',
        borderRadius: '24px',
        background:
          'linear-gradient(180deg, rgba(255,249,242,1) 0%, rgba(255,255,255,1) 100%)',
        p: { xs: '18px', md: '24px' },
        boxShadow: '0 12px 32px rgba(126, 67, 8, 0.06)',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: '14px',
          mb: '20px',
        }}
      >
        <Box
          sx={{
            width: '44px',
            height: '44px',
            borderRadius: '14px',
            backgroundColor: '#fff1e3',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>

        <Box>
          <Typography
            sx={{
              color: '#281302',
              fontSize: '22px',
              fontWeight: 700,
              lineHeight: '30px',
              mb: '4px',
            }}
          >
            {title}
          </Typography>
          <Typography
            sx={{
              color: '#6c5b4c',
              fontSize: '15px',
              lineHeight: '24px',
            }}
          >
            {subtitle}
          </Typography>
        </Box>
      </Box>

      {children}
    </Box>
  );
}

function FieldsGrid({ children }: { children: React.ReactNode }) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', xl: '1fr 1fr' },
        gap: '20px',
        alignItems: 'start',
      }}
    >
      {children}
    </Box>
  );
}

function AdditionalFieldsBlock({ children }: { children: React.ReactNode }) {
  return (
    <Box
      sx={{
        mt: '24px',
        pt: '20px',
        borderTop: '1px solid #eadfd3',
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
      }}
    >
      {children}
    </Box>
  );
}
